import type { ConnectorDefinition } from "../types";
import { freshserviceDomain } from "./freshservice-agent";

/**
 * Declared once so the definition's `system.token.envVar` and the reader below
 * cannot drift apart.
 */
const REQUESTER_API_KEY_ENV = "FRESHSERVICE_SUPPORT_API_KEY";

/**
 * Shared Freshservice *service-account* API key powering the end-user
 * (requester) connector. Unlike the agent connector (`freshservice`,
 * per-user API key), this is a single tenant-wide credential read from the
 * `FRESHSERVICE_SUPPORT_API_KEY` env var — no per-user connect step. The
 * service account is scoped in Freshservice to the requester group; our
 * capability additionally enforces per-requester isolation server-side so an
 * end-user can only ever see their own tickets.
 */
export function freshserviceRequesterApiKey(): string | undefined {
  return process.env[REQUESTER_API_KEY_ENV];
}

/**
 * Built-in, end-user-facing Freshservice connector. `authType: "system"` with
 * an `env`-sourced token, so it materializes for every user from the shared
 * service-account key (resolved generically in `getSystemConnectorToken`, and
 * assembled into the token bag in `getActiveConnectorTokens`) rather than
 * requiring each user to paste a key.
 *
 * Gated org-wide-safe by `connectors.freshservice-requester.enable`: because the
 * capability's tools filter every read to the caller's own tickets
 * (`ctx.userEmail`) and verify ownership before returning a single ticket, this
 * connector can be targeted at all employees without leaking others' tickets.
 * `isEnabled` keeps it dark unless BOTH the shared domain and the service
 * account key are configured.
 */
export const freshserviceRequesterConnector: ConnectorDefinition = {
  id: "freshservice-support",
  name: "Freshservice - Requester",
  description:
    "Check the status of your own IT support tickets, browse the knowledge " +
    "base, and — when enabled — open a new ticket or reply to your own tickets.",
  icon: "freshservice",
  authType: "system",
  kind: "custom",
  system: {
    token: { from: "env", envVar: REQUESTER_API_KEY_ENV },
    resolvesToken: true,
  },
  isEnabled: () => !!freshserviceDomain() && !!freshserviceRequesterApiKey(),
};
